import { toRecord } from '../shared/network-tool-card-utils';
import { unwrapMcpToolOutput } from '../shared/unwrap-mcp-tool-output';

export type QueryFilter = { field: string; op: string; value: unknown };

export type ParsedQuery = {
  collection?: string;
  mode: string;
  filters: QueryFilter[];
  dateRange?: { field: string; from?: string; to?: string };
  groupBy?: { field: string; dateBucket?: string };
  sort?: { field: string; order: string };
  limit?: number;
};

export type GroupBucket = { key: unknown; count: number; label?: string };

export type ParsedResult =
  | { kind: 'count'; count: number }
  | { kind: 'group'; groups: GroupBucket[] }
  | { kind: 'list'; returned: number; rows: unknown[] }
  | { kind: 'validate'; valid: boolean; errors?: string[] }
  | { kind: 'schema'; collections: string[] }
  | { kind: 'unknown'; raw: string };

function coerceOutputObject(output: unknown): Record<string, unknown> | null {
  return toRecord(unwrapMcpToolOutput(output)) ?? null;
}

export function parseQuery(input: unknown): ParsedQuery | null {
  const record = toRecord(input);

  if (!record) return null;
  const rawFilters = Array.isArray(record['filters']) ? record['filters'] : [];
  const filters = rawFilters
    .map((entry) => toRecord(entry))
    .filter((entry): entry is Record<string, unknown> => Boolean(entry))
    .map((entry) => ({
      field: String(entry['field'] ?? ''),
      op: String(entry['op'] ?? ''),
      value: entry['value'],
    }))
    .filter((entry) => entry.field.length > 0);

  const dateRangeRecord = toRecord(record['dateRange']);
  const groupByRecord = toRecord(record['groupBy']);
  const sortRecord = toRecord(record['sort']);

  return {
    collection:
      typeof record['collection'] === 'string'
        ? record['collection']
        : undefined,
    mode: typeof record['mode'] === 'string' ? record['mode'] : 'list',
    filters,
    dateRange: dateRangeRecord
      ? {
          field: String(dateRangeRecord['field'] ?? ''),
          from:
            typeof dateRangeRecord['from'] === 'string'
              ? dateRangeRecord['from']
              : undefined,
          to:
            typeof dateRangeRecord['to'] === 'string'
              ? dateRangeRecord['to']
              : undefined,
        }
      : undefined,
    groupBy: groupByRecord
      ? {
          field: String(groupByRecord['field'] ?? ''),
          dateBucket:
            typeof groupByRecord['dateBucket'] === 'string'
              ? groupByRecord['dateBucket']
              : undefined,
        }
      : undefined,
    sort: sortRecord
      ? {
          field: String(sortRecord['field'] ?? ''),
          order: String(sortRecord['order'] ?? 'desc'),
        }
      : undefined,
    limit: typeof record['limit'] === 'number' ? record['limit'] : undefined,
  };
}

export function parseResult(output: unknown): ParsedResult | null {
  const record = coerceOutputObject(output);

  if (!record) return null;

  if (typeof record['valid'] === 'boolean') {
    const errors = Array.isArray(record['errors'])
      ? record['errors'].filter(
          (entry): entry is string => typeof entry === 'string',
        )
      : undefined;

    return {
      kind: 'validate',
      valid: record['valid'],
      errors,
    };
  }

  if (Array.isArray(record['collections'])) {
    const collections = record['collections']
      .map((entry) => toRecord(entry)?.['collection'])
      .filter((name): name is string => typeof name === 'string');

    return { kind: 'schema', collections };
  }

  if (record['mode'] === 'count' && typeof record['count'] === 'number') {
    return { kind: 'count', count: record['count'] };
  }

  if (record['mode'] === 'group' && Array.isArray(record['groups'])) {
    const groups = record['groups']
      .map((entry) => toRecord(entry))
      .filter((entry): entry is Record<string, unknown> => Boolean(entry))
      .map((entry) => ({
        key: entry['key'],
        count: typeof entry['count'] === 'number' ? entry['count'] : 0,
        label: typeof entry['label'] === 'string' ? entry['label'] : undefined,
      }));

    return { kind: 'group', groups };
  }

  if (record['mode'] === 'list' && Array.isArray(record['rows'])) {
    return {
      kind: 'list',
      returned:
        typeof record['returned'] === 'number'
          ? record['returned']
          : record['rows'].length,
      rows: record['rows'],
    };
  }

  return null;
}
